"use client";

import { useState } from "react";
import type { KundaliRequest } from "@ishvaram/api-client";
import BirthDetailsForm from "@/components/BirthDetailsForm";
import { NAKSHATRAS } from "./types";
import Button from "@ishvaram/ui/Button";
import { BABY_NAMES_COPY, type BabyNamesCopy, type BabyNamesLanguage } from "./copy";

export type BabyNameGenderFilter = "all" | "male" | "female";
type BabyNamesMode = "birth" | "nakshatra";

interface BabyNamesFormProps {
  loading: boolean;
  error?: string | null;
  onBirthSubmit: (data: KundaliRequest) => void;
  onNakshatraSubmit: (data: { nakshatra: string; pada?: number; birthDate: string }) => void;
  genderFilter: BabyNameGenderFilter;
  onGenderFilterChange: (gender: BabyNameGenderFilter) => void;
  language?: BabyNamesLanguage;
}

function BirthModeForm({
  loading,
  error,
  onSubmit,
  language,
}: Pick<BabyNamesFormProps, "loading" | "error"> & {
  onSubmit: BabyNamesFormProps["onBirthSubmit"];
  language: BabyNamesLanguage;
}) {
  return (
    <BirthDetailsForm
      onSubmit={onSubmit}
      loading={loading}
      language={language}
      submitLabel={BABY_NAMES_COPY[language].submit}
      error={error ?? undefined}
      analyticsToolName="baby-names"
      requireBirthTime
      requireResolvedPlace
      collectName={false}
    />
  );
}

function ModeToggle({
  mode,
  onChange,
  copy,
}: {
  mode: BabyNamesMode;
  onChange: (mode: BabyNamesMode) => void;
  copy: BabyNamesCopy;
}) {
  return (
    <div
      className="mb-6 flex overflow-hidden rounded-lg border-[1.5px] border-border-default"
      role="group"
      aria-label={copy.modeLabel}
    >
      {(
        [
          { value: "birth", label: copy.modes.birth },
          { value: "nakshatra", label: copy.modes.nakshatra },
        ] as const
      ).map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={mode === option.value}
          className={`flex-1 py-2.5 text-body-small font-semibold transition-all ${
            mode === option.value
              ? "bg-surface-brand-strong text-text-on-brand"
              : "bg-surface-raised text-text-secondary"
          }`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function GenderFilter({
  value,
  onChange,
  copy,
}: {
  value: BabyNameGenderFilter;
  onChange: (gender: BabyNameGenderFilter) => void;
  copy: BabyNamesCopy;
}) {
  return (
    <div className="mb-4">
      <span className="mb-2 block text-body-small font-semibold text-text-primary">
        {copy.genderHeading}
      </span>
      <div className="flex gap-2" role="group" aria-label={copy.genderLabel}>
        {(
          [
            { value: "all", label: copy.genders.all },
            { value: "male", label: copy.genders.male },
            { value: "female", label: copy.genders.female },
          ] as const
        ).map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={value === option.value}
            className={`flex-1 rounded-lg border-[1.5px] py-2 text-body-small font-semibold transition-all ${
              value === option.value
                ? "border-border-interactive bg-surface-brand-strong text-text-on-brand"
                : "border-border-default bg-surface-raised text-text-secondary"
            }`}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function BabyNamesForm({
  loading,
  error,
  onBirthSubmit,
  onNakshatraSubmit,
  genderFilter,
  onGenderFilterChange,
  language = "en",
}: BabyNamesFormProps) {
  const copy = BABY_NAMES_COPY[language];
  const [mode, setMode] = useState<BabyNamesMode>("birth");
  const [selectedNakshatra, setSelectedNakshatra] = useState("");
  const [selectedPada, setSelectedPada] = useState<string>("");
  const [manualBirthDate, setManualBirthDate] = useState("");

  const handleNakshatraSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onNakshatraSubmit({
      nakshatra: selectedNakshatra,
      pada: selectedPada ? Number(selectedPada) : undefined,
      birthDate: manualBirthDate,
    });
  };

  return (
    <div className="mx-auto max-w-md" data-design-owner="baby-names-form">
      <ModeToggle mode={mode} onChange={setMode} copy={copy} />
      <GenderFilter value={genderFilter} onChange={onGenderFilterChange} copy={copy} />

      {mode === "birth" ? (
        <BirthModeForm
          loading={loading}
          error={error}
          onSubmit={onBirthSubmit}
          language={language}
        />
      ) : (
        <form
          translate="no"
          // The client guard (BabyNamesClient) explains a missing nakshatra or
          // date in the page language; the browser's own bubble is in the
          // browser's language and used to make that guard unreachable.
          noValidate
          onSubmit={handleNakshatraSubmit}
          className="card-sacred p-6 sm:p-8 space-y-5"
        >
          {/* Nakshatra Dropdown */}
          <div>
            <label
              htmlFor="baby-names-nakshatra"
              className="mb-2 block text-body-small font-semibold text-text-primary"
            >
              {copy.nakshatraLabel}
            </label>
            <select
              id="baby-names-nakshatra"
              required
              value={selectedNakshatra}
              onChange={(e) => setSelectedNakshatra(e.target.value)}
              className="input-sacred w-full rounded-lg px-4 py-3 text-body-small text-text-primary"
            >
              <option value="">{copy.nakshatraPlaceholder}</option>
              {NAKSHATRAS.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.name}
                </option>
              ))}
            </select>
          </div>

          {/* Pada */}
          <div>
            <span className="mb-2 block text-body-small font-semibold text-text-primary">
              {copy.pada} <span className="font-normal text-text-muted">{copy.optional}</span>
            </span>
            <div className="flex gap-2" role="group" aria-label={copy.padaLabel}>
              {["", "1", "2", "3", "4"].map((p) => (
                <button
                  key={p}
                  type="button"
                  aria-pressed={selectedPada === p}
                  className={`flex-1 rounded-lg border-[1.5px] py-2 text-body-small font-semibold transition-all ${
                    selectedPada === p
                      ? "border-border-interactive bg-surface-brand-strong text-text-on-brand"
                      : "border-border-default bg-surface-raised text-text-secondary"
                  }`}
                  onClick={() => setSelectedPada(p)}
                >
                  {p || copy.all}
                </button>
              ))}
            </div>
          </div>

          {/* Birth Date (for numerology) */}
          <div>
            <label
              htmlFor="baby-names-birth-date"
              className="mb-2 block text-body-small font-semibold text-text-primary"
            >
              {copy.birthDate}{" "}
              <span className="font-normal text-text-muted">{copy.birthDateHint}</span>
            </label>
            <input
              id="baby-names-birth-date"
              type="date"
              required
              min="1900-01-01"
              max={new Date().toISOString().slice(0, 10)}
              value={manualBirthDate}
              onChange={(e) => setManualBirthDate(e.target.value)}
              className="input-sacred w-full rounded-lg px-4 py-3 text-body-small text-text-primary"
            />
          </div>

          {/* Error */}
          {error && (
            <div
              role="alert"
              className="rounded-lg border border-[var(--intent-error-border)] bg-[var(--intent-error-surface)] p-3.5 text-body-small text-[var(--intent-error-text)]"
            >
              {error}
            </div>
          )}

          {/* Submit */}
          <Button
            type="submit"
            onPress={() => {}}
            loading={loading}
            title={`${copy.submit} →`}
            className="w-full rounded-lg py-3.5 text-button font-semibold"
          />
        </form>
      )}
    </div>
  );
}
